import { useStore } from '@nanostores/react';
import { accountStore, authStore } from './store';

export enum AccountFeatureFlag {
  Whatsapp = 'whatsapp',
  StoreAnonEvents = 'store_anon_events',
  JavascriptWebhook = 'javascript_webhook',
  LegacyCustomSmtpBilling = 'legacy_custom_smtp_billing',
  SubscriptionCenterM1 = 'subscription_center_m1',
  MultiLanguageSettings = 'multi_language_settings',
  ExitOnlyAfterMessage = 'exit_only_after_message',
  CdpEnabled = 'cdp_enabled',
  ParcelAmp = 'parcel_amp',
  CdpDataCatalogEnabled = 'cdp_data_catalog_enabled',
  VirtualSupportChatEnabled = 'virtual_support_chat_enabled',
  ParcelInHydra = 'parcel_in_hydra',
  InAppLegacyEditor = 'in_app_legacy_editor',
  InteractiveDemoWorkspace = 'interactive_demo_workspace', // DEPRECATED
  SampleWorkspace = 'sample_workspace',
  TourtipAdventuresV1 = 'tourtip_adventures_v1',
  FlagCDPUIConsolidation = 'cdp_ui_consolidation',
  AiEnabled = 'ai_enabled',
  GoalsTracking = 'goals-tracking',
  McpExternalAccountToggle = 'mcp-external-account-toggle',
  UniversalSearchDialog = 'universal-search-dialog',
  AiCopilotFullscreen = 'ai-copilot-fullscreen',
  TasksPerformanceDashboard = 'tasks_performance_dashboard_enabled',
  // Pulled from Ember feature flags
  ActivityLogsTimeRange = 'activity-logs-time-range',
  AiAutoSegmentNames = 'ai_auto_segment_names',
  AiDsUpdate = 'ai-ds-update',
  DeliverabilityAnalytics = 'deliverability-analytics',
  DsDbTransactions = 'ds-db-transactions',
  SegmentsInSegments = 'segments-in-segments',
  TransactionalSms = 'transactional-sms',
  LLMSendTimeOptimization = 'llm-send-time-optimization',
  LLMCampaignSendTimeOptimization = 'llm-campaign-send-time-optimization',
  CustomRoles = 'custom-roles',
  NewsletterRateLimitWithTimezone = 'newsletter-rate-limit-timezone',
  InAppEditorLeadForm = 'in-app-editor-lead-form',
  DefaultFromIdentity = 'default-from-identity',
  EmailAutoTls = 'email-auto-tls',
  ConfiguredWebhooks = 'configured_webhooks',
  LineChannel = 'line-channel',
  LLMAction = 'llm-action',
  InAppForms = 'in-app-forms',
  AuditLogs = 'audit-logs',
  AccountVerification = 'account-verification',
  TeamMembersExport = 'team_member_export',
  GlobalExitConditions = 'global-exit-conditions',
  ObjectWaitUntil = 'obj_wait_until',
  JourneyAttributes = 'journey-attributes',
  WhatsAppNative = 'whatsapp-native',
  WhatsappNativeFreeText = 'whatsapp-native-free-text',
  ShowTemplatesSnippetsLayouts = 'show_templates_snippets_layouts',
  InAppGlobalStyles = 'in-app_global-styles',
  HalloweenStickies2025 = 'halloween_stickies_2025',
  HelpInHydra = 'help-in-hydra',
  InboxMessages = 'inbox-messages',
  HydraMasq = 'hydra-masq',
  MultiLanguageEmailTesting = 'multi-language-email-testing',
}

export type FeatureFlag = AccountFeatureFlag;
export const FeatureFlag = { ...AccountFeatureFlag };

export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = Object.freeze({
  whatsapp: false, // whether to enable sending to whatsapp numbers in Twilio SMS action
  store_anon_events: true, // enables the collection of anonymous events.
  javascript_webhook: false, // enables adding javascript reporting webhook
  legacy_custom_smtp_billing: true, // whether to skip message overages for accounts with custom smtp https://github.com/customerio/issues/5947
  subscription_center_m1: false, // enables milestone 0 and milestone 1 features of the subscription center
  multi_language_settings: false, // enables custom multi-language settings
  exit_only_after_message: false, // enables the "exit only after receiving a message" exit option for campaigns
  cdp_enabled: false, // disables cdp
  parcel_amp: true, // enables AMP in Parcel
  cdp_data_catalog_enabled: false, // enabled Data Catalog menu item and page in CDP
  virtual_support_chat_enabled: false, // enables the virtual support chat via Zendesk widget
  parcel_in_hydra: false, // enables Parcel in Hydra
  in_app_legacy_editor: false, // enables the legacy in-app editor
  interactive_demo_workspace: true, // SOON-TO-BE DEPRECATED, can be removed after backend migration, use sample_workspace instead
  sample_workspace: true, // enables the sample workspace
  cdp_ui_consolidation: false, //if enabled, CDP is now presented inside the "Integrations" and no sidebar is displayed
  tourtip_adventures_v1: true,
  ai_enabled: false, // enables the AI features in the app
  'goals-tracking': false, // enables the goals tracking feature
  'mcp-external-account-toggle': false, // mcp external account toggle
  'universal-search-dialog': false, // enables the universal search dialog
  'ai-copilot-fullscreen': false, // enables the AI copilot fullscreen assistant page
  tasks_performance_dashboard_enabled: false, // enables the tasks performance dashboard
  // Ember feature flags
  'activity-logs-time-range': false, // enables the activity logs time range feature
  ai_auto_segment_names: false, // enables the AI auto segment names feature
  'ai-ds-update': false, // enables the AI DS update feature
  'deliverability-analytics': false, // enables the deliverability analytics feature
  'ds-db-transactions': false, // enables the DS DB transactions feature
  'segments-in-segments': false, // enables the segments in segments feature
  'transactional-sms': false, // enables the transactional SMS feature
  'llm-send-time-optimization': false, // enables the LLM send time optimization feature
  'llm-campaign-send-time-optimization': false, // enables the LLM campaign send time optimization feature
  'custom-roles': false, // enables the custom roles feature
  'newsletter-rate-limit-timezone': false, // enables the newsletter rate limit timezone feature
  'in-app-editor-lead-form': false, // enables the in-app editor lead form feature
  'default-from-identity': false, // enables the default from identity feature
  'email-auto-tls': false, // enables the email auto TLS feature
  configured_webhooks: false, // enables the configured webhooks feature
  'line-channel': false, // enables the line channel feature
  'llm-action': false, // enables the LLM action feature
  'in-app-forms': false, // enables the in-app forms feature
  'audit-logs': false, // enables the audit logs feature
  'account-verification': false, // enables the account verification feature
  team_member_export: false, // enables the team member export feature
  'global-exit-conditions': false, // enables the global exit conditions feature
  obj_wait_until: false, // enables the obj wait until feature
  'journey-attributes': false, // enables the journey attributes feature
  'whatsapp-native': false, // enables the WhatsApp native feature
  'whatsapp-native-free-text': false, // enables the WhatsApp native free text feature
  show_templates_snippets_layouts: false, // enables the show templates snippets layouts feature
  'in-app_global-styles': false, // enables the in-app global styles feature
  halloween_stickies_2025: false, // enables the Halloween stickies 2025 feature
  'help-in-hydra': false, // enables the help in hydra feature
  'inbox-messages': false, // enables the ability to author inbox messages
  'hydra-masq': false, // enables the masquerade banner in Hydra React areas
  'multi-language-email-testing': false, // enables the multi-language email testing feature
});

export const useFeatureFlags = () => {
  const account = useStore(accountStore);
  const flags = account?.feature_flags ?? {};

  return Object.assign({}, FEATURE_FLAG_DEFAULTS, flags);
};

export const useFeatureFlag = (flag: FeatureFlag | string) => {
  const flags = useFeatureFlags();
  return flags[flag] ?? false;
};

export const hasFeatureFlag = (flag: FeatureFlag) => {
  const defaultValue = FEATURE_FLAG_DEFAULTS[flag] ?? false;

  const account = authStore.get()?.account;

  if (account == null) {
    return defaultValue;
  }

  return account.feature_flags?.[flag] ?? defaultValue;
};
